import React, { useState, useEffect } from 'react';
import {
  Modal,
  Tabs,
  Table,
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Alert,
  Spin,
  Row,
  Col,
  Statistic,
  Collapse,
  Badge,
  Tooltip,
  Empty,
  Select,
  Divider,
  message,
  Checkbox
} from 'antd';
import {
  SyncOutlined,
  PlusOutlined,
  MinusOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  ApiOutlined,
  AppstoreOutlined,
  BookOutlined,
  DashboardOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { useTheme } from '../App';
import apiClient from '../services/axiosConfig';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { Option } = Select;

// Custom Diff Viewer Component
const DiffViewer = ({ oldValue, newValue, title, currentTheme }) => {
  const { theme } = useTheme();
  
  const formatJson = (value) => {
    try {
      if (typeof value === 'string') {
        return JSON.stringify(JSON.parse(value), null, 2);
      }
      return JSON.stringify(value, null, 2);
    } catch {
      return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value || '');
    }
  };

  const oldFormatted = formatJson(oldValue);
  const newFormatted = formatJson(newValue);

  return (
    <div>
      {title && (
        <Title level={5} style={{ marginBottom: 16, color: theme.text }}>
          {title}
        </Title>
      )}
      <Row gutter={16}>
        <Col span={12}>
          <Card 
            size="small" 
            title={
              <Space>
                <Badge status="error" />
                <Text strong>Production</Text>
              </Space>
            }
            style={{ backgroundColor: theme.surface }}
          >
            <SyntaxHighlighter
              language="json"
              style={currentTheme === 'dark' ? vscDarkPlus : vs}
              customStyle={{
                margin: 0,
                fontSize: '12px',
                backgroundColor: theme.codeBg || theme.surfaceLight,
                maxHeight: '400px',
                overflow: 'auto'
              }}
            >
              {oldFormatted || '// Empty'}
            </SyntaxHighlighter>
          </Card>
        </Col>
        <Col span={12}>
          <Card 
            size="small" 
            title={
              <Space>
                <Badge status="processing" />
                <Text strong>Development</Text>
              </Space>
            }
            style={{ backgroundColor: theme.surface }}
          >
            <SyntaxHighlighter
              language="json"
              style={currentTheme === 'dark' ? vscDarkPlus : vs}
              customStyle={{
                margin: 0,
                fontSize: '12px',
                backgroundColor: theme.codeBg || theme.surfaceLight,
                maxHeight: '400px',
                overflow: 'auto'
              }}
            >
              {newFormatted || '// Empty'}
            </SyntaxHighlighter>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

const DiffChecker = ({ visible, onClose }) => {
  const { theme, currentTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [diffData, setDiffData] = useState(null);
  const [selectedApi, setSelectedApi] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedValidationRule, setSelectedValidationRule] = useState(null);
  const [filterType, setFilterType] = useState('all'); // all, added, modified, removed
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [publishingSelective, setPublishingSelective] = useState(false);
  
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const fetchDiffData = async () => {
    setLoading(true);
    try {
      // Use the diff routes that already exist in your backend!
      const response = await apiClient.get(`${API_BASE_URL}/diff/full`);
      
      const diffData = response.data;
      
      // Transform the response to match your component's expected structure
      const diff = {
        apis: diffData.apis,
        groups: diffData.groups,
        docs: diffData.documentation,
        recipes: diffData.recipes,
        validationRules: diffData.validationRules,
        summary: {
          totalChanges: diffData.overallSummary.totalChanges,
          added: diffData.overallSummary.totalAdded,
          modified: diffData.overallSummary.totalModified,
          removed: diffData.overallSummary.totalRemoved
        }
      };
      
      console.log('Diff results from API:');
      console.log('APIs - Added:', diff.apis.added.length, 'Modified:', diff.apis.modified.length, 'Removed:', diff.apis.removed.length);
      console.log('Validation Rules - Added:', diff.validationRules.added.length, 'Modified:', diff.validationRules.modified.length, 'Removed:', diff.validationRules.removed.length);
      console.log('Dev total:', diff.apis.summary.devCount, 'Prod total:', diff.apis.summary.prodCount);
      
      setDiffData(diff);
    } catch (error) {
      message.error('Failed to fetch diff data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchDiffData();
    }
  }, [visible]);

  const renderSummary = () => {
    if (!diffData) return null;

    return (
      <div>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Changes"
                value={diffData.summary.totalChanges}
                prefix={<SyncOutlined />}
                valueStyle={{ color: theme.primary }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Added"
                value={diffData.summary.added}
                prefix={<PlusOutlined />}
                valueStyle={{ color: theme.success }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Modified"
                value={diffData.summary.modified}
                prefix={<EditOutlined />}
                valueStyle={{ color: theme.accent }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Removed"
                value={diffData.summary.removed}
                prefix={<MinusOutlined />}
                valueStyle={{ color: theme.error }}
              />
            </Card>
          </Col>
        </Row>

        <Card title="Changes by Category">
          <Table
            dataSource={[
              {
                category: 'APIs',
                icon: <ApiOutlined />,
                added: diffData.apis.added.length,
                modified: diffData.apis.modified.length,
                removed: diffData.apis.removed.length,
                total: diffData.apis.added.length + diffData.apis.modified.length + diffData.apis.removed.length,
                devCount: diffData.apis.summary.devCount,
                prodCount: diffData.apis.summary.prodCount
              },
              {
                category: 'Groups',
                icon: <AppstoreOutlined />,
                added: diffData.groups.added.length,
                modified: diffData.groups.modified.length,
                removed: diffData.groups.removed.length,
                total: diffData.groups.added.length + diffData.groups.modified.length + diffData.groups.removed.length,
                devCount: diffData.groups.summary.devCount,
                prodCount: diffData.groups.summary.prodCount
              },
              {
                category: 'Documentation',
                icon: <FileTextOutlined />,
                added: diffData.docs.added.length,
                modified: diffData.docs.modified.length,
                removed: diffData.docs.removed.length,
                total: diffData.docs.added.length + diffData.docs.modified.length + diffData.docs.removed.length,
                devCount: diffData.docs.summary.devCount,
                prodCount: diffData.docs.summary.prodCount
              },
              {
                category: 'Recipes',
                icon: <BookOutlined />,
                added: diffData.recipes.added.length,
                modified: diffData.recipes.modified.length,
                removed: diffData.recipes.removed.length,
                total: diffData.recipes.added.length + diffData.recipes.modified.length + diffData.recipes.removed.length,
                devCount: diffData.recipes.summary.devCount,
                prodCount: diffData.recipes.summary.prodCount
              },
              {
                category: 'Validation Rules',
                icon: <SafetyCertificateOutlined />,
                added: diffData.validationRules.added.length,
                modified: diffData.validationRules.modified.length,
                removed: diffData.validationRules.removed.length,
                total: diffData.validationRules.added.length + diffData.validationRules.modified.length + diffData.validationRules.removed.length,
                devCount: diffData.validationRules.summary.devCount,
                prodCount: diffData.validationRules.summary.prodCount
              }
            ]}
            columns={[
              {
                title: 'Category',
                dataIndex: 'category',
                key: 'category',
                render: (text, record) => (
                  <Space>
                    {record.icon}
                    <Text strong>{text}</Text>
                  </Space>
                )
              },
              {
                title: 'Added',
                dataIndex: 'added',
                key: 'added',
                render: (val) => val > 0 ? <Tag color="green">+{val}</Tag> : <Tag>0</Tag>
              },
              {
                title: 'Modified',
                dataIndex: 'modified',
                key: 'modified',
                render: (val) => val > 0 ? <Tag color="orange">~{val}</Tag> : <Tag>0</Tag>
              },
              {
                title: 'Removed',
                dataIndex: 'removed',
                key: 'removed',
                render: (val) => val > 0 ? <Tag color="red">-{val}</Tag> : <Tag>0</Tag>
              },
              {
                title: 'Total Changes',
                dataIndex: 'total',
                key: 'total',
                render: (val) => <Text strong>{val}</Text>
              },
              {
                title: 'Dev/Prod Count',
                key: 'counts',
                render: (_, record) => (
                  <Text type="secondary">{record.devCount} / {record.prodCount}</Text>
                )
              }
            ]}
            pagination={false}
            rowKey="category"
          />
        </Card>
      </div>
    );
  };

  const renderApiDiff = () => {
    if (!diffData) return null;

    const filterData = () => {
      let data = [];
      if (filterType === 'all' || filterType === 'added') {
        data = [...data, ...diffData.apis.added.map(item => ({ ...item, changeType: 'added' }))];
      }
      if (filterType === 'all' || filterType === 'modified') {
        data = [...data, ...diffData.apis.modified.map(item => ({ ...item, changeType: 'modified' }))];
      }
      if (filterType === 'all' || filterType === 'removed') {
        data = [...data, ...diffData.apis.removed.map(item => ({ ...item, changeType: 'removed' }))];
      }
      return data;
    };

    const columns = [
      {
        title: 'Change',
        key: 'changeType',
        width: 100,
        render: (_, record) => {
          const changeType = record.changeType;
          return (
            <Tag
              color={
                changeType === 'added' ? 'green' :
                changeType === 'modified' ? 'orange' :
                'red'
              }
            >
              {changeType === 'added' ? 'NEW' :
               changeType === 'modified' ? 'MODIFIED' :
               'REMOVED'}
            </Tag>
          );
        }
      },
      {
        title: 'API Name',
        key: 'name',
        render: (_, record) => {
          const item = record.dev || record.prod;
          return <Text strong>{item.name}</Text>;
        }
      },
      {
        title: 'Group',
        key: 'group',
        render: (_, record) => {
          const item = record.dev || record.prod;
          return <Tag color="purple">{item.group}</Tag>;
        }
      },
      {
        title: 'Route',
        key: 'route',
        render: (_, record) => {
          const item = record.dev || record.prod;
          return <Text code>{item.route}</Text>;
        }
      },
      {
        title: 'Method',
        key: 'method',
        width: 80,
        render: (_, record) => {
          const item = record.dev || record.prod;
          return (
            <Tag color={
              item.method === 'GET' ? 'green' :
              item.method === 'POST' ? 'blue' :
              item.method === 'PUT' ? 'orange' :
              'red'
            }>
              {item.method}
            </Tag>
          );
        }
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        render: (_, record) => (
          <Space>
            <Tooltip title="View Details">
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => setSelectedApi(record)}
                size="small"
              >
                Details
              </Button>
            </Tooltip>
          </Space>
        )
      }
    ];

    return (
      <div>
        {/* Summary Card */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title="Dev Total"
                value={diffData.apis.summary.devCount}
                valueStyle={{ color: theme.primary }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Prod Total"
                value={diffData.apis.summary.prodCount}
                valueStyle={{ color: theme.primary }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Difference"
                value={diffData.apis.summary.devCount - diffData.apis.summary.prodCount}
                valueStyle={{ color: diffData.apis.summary.devCount > diffData.apis.summary.prodCount ? theme.success : theme.error }}
                prefix={diffData.apis.summary.devCount > diffData.apis.summary.prodCount ? '+' : ''}
              />
            </Col>
            <Col span={6}>
              <div style={{ fontSize: '12px', color: theme.textSecondary }}>
                <div>Added: {diffData.apis.added.length}</div>
                <div>Modified: {diffData.apis.modified.length}</div>
                <div>Removed: {diffData.apis.removed.length}</div>
                <div>Unchanged: {diffData.apis.unchanged.length}</div>
              </div>
            </Col>
          </Row>
        </Card>

        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Select
            value={filterType}
            onChange={setFilterType}
            style={{ width: 200 }}
          >
            <Option value="all">All Changes</Option>
            <Option value="added">Added Only</Option>
            <Option value="modified">Modified Only</Option>
            <Option value="removed">Removed Only</Option>
          </Select>
          <Space>
            <Text>Showing: {filterData().length} items</Text>
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={filterData()}
          rowKey={(record) => (record.dev || record.prod)._id}
          pagination={{ pageSize: 20 }}
        />
      </div>
    );
  };

  const renderGroupDiff = () => {
    if (!diffData) return null;

    const filterData = () => {
      let data = [];
      if (filterType === 'all' || filterType === 'added') {
        data = [...data, ...diffData.groups.added.map(item => ({ ...item, changeType: 'added' }))];
      }
      if (filterType === 'all' || filterType === 'modified') {
        data = [...data, ...diffData.groups.modified.map(item => ({ ...item, changeType: 'modified' }))];
      }
      if (filterType === 'all' || filterType === 'removed') {
        data = [...data, ...diffData.groups.removed.map(item => ({ ...item, changeType: 'removed' }))];
      }
      return data;
    };

    const columns = [
      {
        title: 'Change',
        key: 'changeType',
        width: 100,
        render: (_, record) => {
          const changeType = record.changeType;
          return (
            <Tag
              color={
                changeType === 'added' ? 'green' :
                changeType === 'modified' ? 'orange' :
                'red'
              }
            >
              {changeType === 'added' ? 'NEW' :
               changeType === 'modified' ? 'MODIFIED' :
               'REMOVED'}
            </Tag>
          );
        }
      },
      {
        title: 'Group Name',
        key: 'name',
        render: (_, record) => {
          const item = record.dev || record.prod;
          return <Text strong>{item.name}</Text>;
        }
      },
      {
        title: 'Description',
        key: 'description',
        render: (_, record) => {
          const item = record.dev || record.prod;
          return <Text>{item.description || '-'}</Text>;
        }
      },
      {
        title: 'Sort Order',
        key: 'sortOrder',
        width: 100,
        render: (_, record) => {
          const item = record.dev || record.prod;
          return <Text>{item.sortOrder || 0}</Text>;
        }
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        render: (_, record) => (
          <Space>
            <Tooltip title="View Details">
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => setSelectedGroup(record)}
                size="small"
              >
                Details
              </Button>
            </Tooltip>
          </Space>
        )
      }
    ];

    return (
      <div>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Select
            value={filterType}
            onChange={setFilterType}
            style={{ width: 200 }}
          >
            <Option value="all">All Changes</Option>
            <Option value="added">Added Only</Option>
            <Option value="modified">Modified Only</Option>
            <Option value="removed">Removed Only</Option>
          </Select>
          <Space>
            <Text>Total: {filterData().length} items</Text>
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={filterData()}
          rowKey={(record) => (record.dev || record.prod)._id}
          pagination={{ pageSize: 20 }}
        />
      </div>
    );
  };

  const renderDocDiff = () => {
    if (!diffData) return null;

    const filterData = () => {
      let data = [];
      if (filterType === 'all' || filterType === 'added') {
        data = [...data, ...diffData.docs.added.map(item => ({ ...item, changeType: 'added' }))];
      }
      if (filterType === 'all' || filterType === 'modified') {
        data = [...data, ...diffData.docs.modified.map(item => ({ ...item, changeType: 'modified' }))];
      }
      if (filterType === 'all' || filterType === 'removed') {
        data = [...data, ...diffData.docs.removed.map(item => ({ ...item, changeType: 'removed' }))];
      }
      return data;
    };

    const columns = [
      {
        title: 'Change',
        key: 'changeType',
        width: 100,
        render: (_, record) => {
          const changeType = record.changeType;
          return (
            <Tag
              color={
                changeType === 'added' ? 'green' :
                changeType === 'modified' ? 'orange' :
                'red'
              }
            >
              {changeType === 'added' ? 'NEW' :
               changeType === 'modified' ? 'MODIFIED' :
               'REMOVED'}
            </Tag>
          );
        }
      },
      {
        title: 'Title',
        key: 'title',
        render: (_, record) => {
          const item = record.dev || record.prod;
          return <Text strong>{item.title}</Text>;
        }
      },
      {
        title: 'Category',
        key: 'category',
        render: (_, record) => {
          const item = record.dev || record.prod;
          return <Tag color="blue">{item.category}</Tag>;
        }
      },
      {
        title: 'Tags',
        key: 'tags',
        render: (_, record) => {
          const item = record.dev || record.prod;
          return (
            <Space size="small">
              {item.tags?.slice(0, 3).map(tag => (
                <Tag key={tag}>{tag}</Tag>
              ))}
              {item.tags?.length > 3 && <Text>+{item.tags.length - 3} more</Text>}
            </Space>
          );
        }
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        render: (_, record) => (
          <Space>
            <Tooltip title="View Details">
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => setSelectedDoc(record)}
                size="small"
              >
                Details
              </Button>
            </Tooltip>
          </Space>
        )
      }
    ];

    return (
      <div>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Select
            value={filterType}
            onChange={setFilterType}
            style={{ width: 200 }}
          >
            <Option value="all">All Changes</Option>
            <Option value="added">Added Only</Option>
            <Option value="modified">Modified Only</Option>
            <Option value="removed">Removed Only</Option>
          </Select>
          <Space>
            <Text>Total: {filterData().length} items</Text>
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={filterData()}
          rowKey={(record) => (record.dev || record.prod)._id}
          pagination={{ pageSize: 20 }}
        />
      </div>
    );
  };

  const renderRecipeDiff = () => {
    if (!diffData) return null;

    const filterData = () => {
      let data = [];
      if (filterType === 'all' || filterType === 'added') {
        data = [...data, ...diffData.recipes.added.map(item => ({ ...item, changeType: 'added' }))];
      }
      if (filterType === 'all' || filterType === 'modified') {
        data = [...data, ...diffData.recipes.modified.map(item => ({ ...item, changeType: 'modified' }))];
      }
      if (filterType === 'all' || filterType === 'removed') {
        data = [...data, ...diffData.recipes.removed.map(item => ({ ...item, changeType: 'removed' }))];
      }
      return data;
    };

    const columns = [
      {
        title: 'Change',
        key: 'changeType',
        width: 100,
        render: (_, record) => {
          const changeType = record.changeType;
          return (
            <Tag
              color={
                changeType === 'added' ? 'green' :
                changeType === 'modified' ? 'orange' :
                'red'
              }
            >
              {changeType === 'added' ? 'NEW' :
               changeType === 'modified' ? 'MODIFIED' :
               'REMOVED'}
            </Tag>
          );
        }
      },
      {
        title: 'Recipe Name',
        key: 'name',
        render: (_, record) => {
          const item = record.dev || record.prod;
          return <Text strong>{item.name}</Text>;
        }
      },
      {
        title: 'Description',
        key: 'description',
        render: (_, record) => {
          const item = record.dev || record.prod;
          return <Text>{item.description || '-'}</Text>;
        },
        ellipsis: true
      },
      {
        title: 'Steps',
        key: 'steps',
        width: 80,
        render: (_, record) => {
          const item = record.dev || record.prod;
          return <Text>{item.steps?.length || 0} steps</Text>;
        }
      },
      {
        title: 'Tags',
        key: 'tags',
        render: (_, record) => {
          const item = record.dev || record.prod;
          return (
            <Space size="small">
              {item.tags?.slice(0, 2).map(tag => (
                <Tag key={tag}>{tag}</Tag>
              ))}
              {item.tags?.length > 2 && <Text>+{item.tags.length - 2}</Text>}
            </Space>
          );
        }
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        render: (_, record) => (
          <Space>
            <Tooltip title="View Details">
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => setSelectedRecipe(record)}
                size="small"
              >
                Details
              </Button>
            </Tooltip>
          </Space>
        )
      }
    ];

    return (
      <div>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Select
            value={filterType}
            onChange={setFilterType}
            style={{ width: 200 }}
          >
            <Option value="all">All Changes</Option>
            <Option value="added">Added Only</Option>
            <Option value="modified">Modified Only</Option>
            <Option value="removed">Removed Only</Option>
          </Select>
          <Space>
            <Text>Total: {filterData().length} items</Text>
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={filterData()}
          rowKey={(record) => (record.dev || record.prod)._id}
          pagination={{ pageSize: 20 }}
        />
      </div>
    );
  };

  const renderValidationRuleDiff = () => {
    if (!diffData || !diffData.validationRules) return null;

    const filterData = () => {
      let data = [];
      if (filterType === 'all' || filterType === 'added') {
        data = [...data, ...diffData.validationRules.added.map(item => ({ ...item, changeType: 'added' }))];
      }
      if (filterType === 'all' || filterType === 'modified') {
        data = [...data, ...diffData.validationRules.modified.map(item => ({ ...item, changeType: 'modified' }))];
      }
      if (filterType === 'all' || filterType === 'removed') {
        data = [...data, ...diffData.validationRules.removed.map(item => ({ ...item, changeType: 'removed' }))];
      }
      return data;
    };

    const columns = [
      {
        title: 'Change',
        key: 'changeType',
        width: 100,
        render: (_, record) => {
          const changeType = record.changeType;
          return (
            <Tag
              color={
                changeType === 'added' ? 'green' :
                changeType === 'modified' ? 'orange' :
                'red'
              }
            >
              {changeType === 'added' ? 'NEW' :
               changeType === 'modified' ? 'MODIFIED' :
               'REMOVED'}
            </Tag>
          );
        }
      },
      {
        title: 'Rule ID',
        key: 'ruleId',
        render: (_, record) => {
          const item = record.dev || record.prod;
          return <Text strong>{item.ruleId}</Text>;
        }
      },
      {
        title: 'Type',
        key: 'type',
        render: (_, record) => {
          const item = record.dev || record.prod;
          return <Tag color="cyan">{item.type}</Tag>;
        }
      },
      {
        title: 'Description',
        key: 'description',
        render: (_, record) => {
          const item = record.dev || record.prod;
          return <Text>{item.description}</Text>;
        },
        ellipsis: true
      },
      {
        title: 'Status',
        key: 'isActive',
        width: 100,
        render: (_, record) => {
          const item = record.dev || record.prod;
          return item.isActive ? 
            <Tag color="green">Active</Tag> : 
            <Tag color="default">Inactive</Tag>;
        }
      },
      {
        title: 'Priority',
        key: 'priority',
        width: 80,
        render: (_, record) => {
          const item = record.dev || record.prod;
          return <Text>{item.priority || 100}</Text>;
        }
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        render: (_, record) => (
          <Space>
            <Tooltip title="View Details">
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => setSelectedValidationRule(record)}
                size="small"
              >
                Details
              </Button>
            </Tooltip>
          </Space>
        )
      }
    ];

    return (
      <div>
        {/* Summary Card */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title="Dev Total"
                value={diffData.validationRules.summary.devCount}
                valueStyle={{ color: theme.primary }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Prod Total"
                value={diffData.validationRules.summary.prodCount}
                valueStyle={{ color: theme.primary }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Difference"
                value={diffData.validationRules.summary.devCount - diffData.validationRules.summary.prodCount}
                valueStyle={{ color: diffData.validationRules.summary.devCount > diffData.validationRules.summary.prodCount ? theme.success : theme.error }}
                prefix={diffData.validationRules.summary.devCount > diffData.validationRules.summary.prodCount ? '+' : ''}
              />
            </Col>
            <Col span={6}>
              <div style={{ fontSize: '12px', color: theme.textSecondary }}>
                <div>Added: {diffData.validationRules.added.length}</div>
                <div>Modified: {diffData.validationRules.modified.length}</div>
                <div>Removed: {diffData.validationRules.removed.length}</div>
                <div>Unchanged: {diffData.validationRules.unchanged.length}</div>
              </div>
            </Col>
          </Row>
        </Card>

        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Select
            value={filterType}
            onChange={setFilterType}
            style={{ width: 200 }}
          >
            <Option value="all">All Changes</Option>
            <Option value="added">Added Only</Option>
            <Option value="modified">Modified Only</Option>
            <Option value="removed">Removed Only</Option>
          </Select>
          <Space>
            <Text>Showing: {filterData().length} items</Text>
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={filterData()}
          rowKey={(record) => (record.dev || record.prod)._id || record.key}
          pagination={{ pageSize: 20 }}
        />
      </div>
    );
  };

  const renderDetailModal = () => {
    const item = selectedApi || selectedDoc || selectedRecipe || selectedGroup || selectedValidationRule;
    if (!item) return null;

    const type = selectedApi ? 'API' : 
                 selectedDoc ? 'Documentation' : 
                 selectedRecipe ? 'Recipe' : 
                 selectedGroup ? 'Group' :
                 'Validation Rule';
    const devItem = item.dev;
    const prodItem = item.prod;
    const isAdded = !prodItem;
    const isRemoved = !devItem;

    return (
      <Modal
        title={
          <Space>
            <Text>{type} Diff:</Text>
            <Text strong>{(devItem || prodItem).name || (devItem || prodItem).title || (devItem || prodItem).ruleId}</Text>
            {isAdded && <Tag color="green">NEW</Tag>}
            {isRemoved && <Tag color="red">REMOVED</Tag>}
            {!isAdded && !isRemoved && <Tag color="orange">MODIFIED</Tag>}
          </Space>
        }
        visible={!!item}
        onCancel={() => {
          setSelectedApi(null);
          setSelectedDoc(null);
          setSelectedRecipe(null);
          setSelectedGroup(null);
          setSelectedValidationRule(null);
        }}
        width={1200}
        footer={[
          <Button key="close" onClick={() => {
            setSelectedApi(null);
            setSelectedDoc(null);
            setSelectedRecipe(null);
            setSelectedGroup(null);
            setSelectedValidationRule(null);
          }}>
            Close
          </Button>
        ]}
      >
        {item.changes && item.changes.length > 0 ? (
          <div>
            <Alert
              message={`${item.changes.length} field(s) changed`}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Collapse defaultActiveKey={['0']}>
              {item.changes.map((change, index) => (
                <Panel
                  key={index}
                  header={
                    <Space>
                      <Text strong>{change.field}</Text>
                      <Tag color={
                        change.type === 'added' ? 'green' :
                        change.type === 'removed' ? 'red' :
                        'orange'
                      }>
                        {change.type}
                      </Tag>
                    </Space>
                  }
                >
                  <DiffViewer
                    oldValue={change.prodValue}
                    newValue={change.devValue}
                    currentTheme={currentTheme}
                  />
                </Panel>
              ))}
            </Collapse>
          </div>
        ) : (
          <DiffViewer
            oldValue={prodItem}
            newValue={devItem}
            title={isAdded ? "New Item" : isRemoved ? "Removed Item" : "Complete Diff"}
            currentTheme={currentTheme}
          />
        )}
      </Modal>
    );
  };

  const exportDiffReport = () => {
    if (!diffData) return;

    const report = {
      timestamp: new Date().toISOString(),
      summary: diffData.summary,
      details: {
        apis: {
          added: diffData.apis.added.map(item => item.dev),
          modified: diffData.apis.modified.map(item => ({
            name: item.dev.name,
            changes: item.changes
          })),
          removed: diffData.apis.removed.map(item => item.prod)
        },
        groups: {
          added: diffData.groups.added.map(item => item.dev),
          modified: diffData.groups.modified.map(item => ({
            name: item.dev.name,
            changes: item.changes
          })),
          removed: diffData.groups.removed.map(item => item.prod)
        },
        documentation: {
          added: diffData.docs.added.map(item => item.dev),
          modified: diffData.docs.modified.map(item => ({
            title: item.dev.title,
            changes: item.changes
          })),
          removed: diffData.docs.removed.map(item => item.prod)
        },
        recipes: {
          added: diffData.recipes.added.map(item => item.dev),
          modified: diffData.recipes.modified.map(item => ({
            name: item.dev.name,
            changes: item.changes
          })),
          removed: diffData.recipes.removed.map(item => item.prod)
        },
        validationRules: {
          added: diffData.validationRules.added.map(item => item.dev),
          modified: diffData.validationRules.modified.map(item => ({
            ruleId: item.dev.ruleId,
            changes: item.changes
          })),
          removed: diffData.validationRules.removed.map(item => item.prod)
        }
      }
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diff-report-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    message.success('Diff report exported successfully');
  };

  return (
    <>
      <Modal
        title={
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            paddingRight: '40px' // Add padding to prevent overlap with close button
          }}>
            <Space>
              <SyncOutlined style={{ fontSize: 20, color: theme.primary }} />
              <Title level={4} style={{ margin: 0 }}>
                Development vs Production Diff
              </Title>
            </Space>
            <Space>
              <Button
                icon={<SyncOutlined />}
                onClick={fetchDiffData}
                loading={loading}
                size="small" // Make buttons smaller if needed
              >
                Refresh
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={exportDiffReport}
                disabled={!diffData}
                size="small" // Make buttons smaller if needed
              >
                Export Report
              </Button>
            </Space>
          </div>
        }
        visible={visible}
        onCancel={onClose}
        width={1400}
        footer={null}
        style={{ top: 20 }}
        bodyStyle={{ padding: 0 }}
        // Alternative: Use custom close icon positioning
        closeIcon={
          <span style={{ 
            position: 'absolute', 
            right: '16px', 
            top: '16px',
            fontSize: '16px',
            cursor: 'pointer'
          }}>
            ✕
          </span>
        }
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" tip="Comparing environments..." />
          </div>
        ) : diffData ? (
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <Tabs.TabPane 
              tab={
                <Space>
                  <DashboardOutlined />
                  Summary
                  <Badge count={diffData.summary.totalChanges} />
                </Space>
              } 
              key="summary"
            >
              <div style={{ padding: '24px' }}>
                {renderSummary()}
              </div>
            </Tabs.TabPane>
            
            <Tabs.TabPane 
              tab={
                <Space>
                  <ApiOutlined />
                  APIs
                  <Badge count={
                    diffData.apis.added.length + 
                    diffData.apis.modified.length + 
                    diffData.apis.removed.length
                  } />
                </Space>
              } 
              key="apis"
            >
              <div style={{ padding: '24px' }}>
                {renderApiDiff()}
              </div>
            </Tabs.TabPane>
            
            <Tabs.TabPane 
              tab={
                <Space>
                  <AppstoreOutlined />
                  Groups
                  <Badge count={
                    diffData.groups.added.length + 
                    diffData.groups.modified.length + 
                    diffData.groups.removed.length
                  } />
                </Space>
              } 
              key="groups"
            >
              <div style={{ padding: '24px' }}>
                {renderGroupDiff()}
              </div>
            </Tabs.TabPane>
            
            <Tabs.TabPane 
              tab={
                <Space>
                  <FileTextOutlined />
                  Documentation
                  <Badge count={
                    diffData.docs.added.length + 
                    diffData.docs.modified.length + 
                    diffData.docs.removed.length
                  } />
                </Space>
              } 
              key="docs"
            >
              <div style={{ padding: '24px' }}>
                {renderDocDiff()}
              </div>
            </Tabs.TabPane>
            
            <Tabs.TabPane 
              tab={
                <Space>
                  <BookOutlined />
                  Recipes
                  <Badge count={
                    diffData.recipes.added.length + 
                    diffData.recipes.modified.length + 
                    diffData.recipes.removed.length
                  } />
                </Space>
              } 
              key="recipes"
            >
              <div style={{ padding: '24px' }}>
                {renderRecipeDiff()}
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane 
              tab={
                <Space>
                  <SafetyCertificateOutlined />
                  Validation Rules
                  <Badge count={
                    (diffData.validationRules?.added?.length || 0) + 
                    (diffData.validationRules?.modified?.length || 0) + 
                    (diffData.validationRules?.removed?.length || 0)
                  } />
                </Space>
              } 
              key="validationRules"
            >
              <div style={{ padding: '24px' }}>
                {renderValidationRuleDiff()}
              </div>
            </Tabs.TabPane>
          </Tabs>
        ) : (
          <Empty description="No diff data available" />
        )}
      </Modal>
      {renderDetailModal()}
    </>
  );
};

export default DiffChecker;